import { useState } from "react";
import { X, Database } from "lucide-react";
import erpData from "@/flows/erp-data.json";
import crmData from "@/flows/crm-data.json";
import treasuryData from "@/flows/treasury-data.json";

const SOURCES = [
  {
    id: "erp",
    label: "ERP",
    description: "SAP ERP — Accounts Receivable postings",
    data: erpData,
    amountKey: "Gross_Amount",
    columns: [
      { key: "Posting_Date",    label: "Date" },
      { key: "Document_No",     label: "Document" },
      { key: "Customer_Name",   label: "Customer" },
      { key: "Invoice_No",      label: "Invoice" },
      { key: "Business_Unit",   label: "Business Unit" },
      { key: "Net_Amount",      label: "Net (INR)" },
      { key: "Tax_Amount",      label: "Tax (INR)" },
      { key: "Gross_Amount",    label: "Gross (INR)" },
      { key: "Posting_Status",  label: "Status" },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    description: "CRM Billing System — Invoice records",
    data: crmData,
    amountKey: "Invoice_Value_Incl_Tax",
    columns: [
      { key: "Billing_Date",            label: "Date" },
      { key: "Billing_System_ID",       label: "Billing ID" },
      { key: "Account_Name",            label: "Account" },
      { key: "Invoice_Number",          label: "Invoice" },
      { key: "Sales_Order",             label: "Sales Order" },
      { key: "Business_Segment",        label: "Segment" },
      { key: "Invoice_Value_Excl_Tax",  label: "Net (INR)" },
      { key: "GST_Value",               label: "GST (INR)" },
      { key: "Invoice_Value_Incl_Tax",  label: "Total (INR)" },
      { key: "Billing_Status",          label: "Status" },
    ],
  },
  {
    id: "treasury",
    label: "Treasury",
    description: "Bank statement — settlement receipts",
    data: treasuryData,
    amountKey: "Credit_Amount",
    columns: [
      { key: "Bank_Value_Date",       label: "Date" },
      { key: "Bank_Reference",        label: "Bank Ref" },
      { key: "UTR_No",                label: "UTR No" },
      { key: "Payer_Name",            label: "Payer" },
      { key: "Narration",             label: "Narration" },
      { key: "Bank_Account",          label: "Bank Account" },
      { key: "Credit_Amount",         label: "Credit (INR)" },
      { key: "Matched_Invoice_Ref",   label: "Invoice Ref" },
      { key: "Settlement_Status",     label: "Status" },
    ],
  },
];

const AMOUNT_COLS = new Set([
  "Net_Amount", "Tax_Amount", "Gross_Amount",
  "Invoice_Value_Excl_Tax", "GST_Value", "Invoice_Value_Incl_Tax",
  "Credit_Amount", "Debit_Amount",
]);

const STATUS_COLS = new Set(["Posting_Status", "Billing_Status", "Settlement_Status"]);

function parseAmount(val) {
  if (!val) return 0;
  return parseInt(String(val).replace(/,/g, ""), 10) || 0;
}

function fmtAmount(val) {
  const n = parseAmount(val);
  return "₹" + n.toLocaleString("en-IN");
}

function sumAmount(data, key) {
  return data.reduce((acc, row) => acc + parseAmount(row[key]), 0);
}

function StatusBadge({ value }) {
  const v = (value || "").toLowerCase();
  const cls = v === "posted" || v === "final" || v === "settled"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-zinc-100 text-zinc-500 border-zinc-200";
  return (
    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide ${cls}`}>
      {value}
    </span>
  );
}

export function SourceDataViewer({ onClose }) {
  const [activeTab, setActiveTab] = useState("erp");
  const source = SOURCES.find((s) => s.id === activeTab);
  const total = sumAmount(source.data, source.amountKey);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <Database className="h-5 w-5 text-zinc-400" />
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Source Data</p>
            <h1 className="text-lg font-medium tracking-tight text-zinc-900">Flow Input Datasets</h1>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-md border border-zinc-200 bg-white p-2 text-zinc-500 shadow-sm transition-colors hover:bg-zinc-50 hover:text-zinc-900"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-200 bg-white px-6">
        {SOURCES.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveTab(s.id)}
            className={`relative px-4 py-3 font-mono text-sm font-medium transition-colors ${
              activeTab === s.id
                ? "text-zinc-900 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-zinc-900"
                : "text-zinc-400 hover:text-zinc-700"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Summary bar */}
      <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50 px-6 py-3">
        <p className="font-mono text-xs text-zinc-500">{source.description}</p>
        <div className="flex items-center gap-6 font-mono text-xs text-zinc-500">
          <span><span className="font-medium text-zinc-900">{source.data.length}</span> records</span>
          <span>
            Total {source.amountKey.includes("Credit") ? "settled" : "invoiced"}:{" "}
            <span className="font-medium text-zinc-900">₹{total.toLocaleString("en-IN")}</span>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-left">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-zinc-200">
              {source.columns.map((col) => (
                <th
                  key={col.key}
                  className="whitespace-nowrap px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {source.data.map((row, i) => (
              <tr
                key={i}
                className={`border-b border-zinc-50 transition-colors hover:bg-zinc-50 ${
                  i % 2 === 0 ? "bg-white" : "bg-zinc-50/40"
                }`}
              >
                {source.columns.map((col) => (
                  <td key={col.key} className="whitespace-nowrap px-4 py-2.5">
                    {STATUS_COLS.has(col.key) ? (
                      <StatusBadge value={row[col.key]} />
                    ) : AMOUNT_COLS.has(col.key) ? (
                      <span className="font-mono text-xs text-zinc-900">{fmtAmount(row[col.key])}</span>
                    ) : (
                      <span className="font-mono text-xs text-zinc-700">{row[col.key] || "—"}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
